import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Autocomplete, Modal, useMediaQuery, Table, TableBody, TableCell, TableContainer, TableHead, FormControlLabel, Checkbox, TableRow, TablePagination, Button, Grid, TextField, IconButton, Tooltip, Box, Tabs, Tab, Chip, Menu, MenuItem, Badge, Stack, Typography, ButtonGroup, ListItemIcon, ListItemText, Select, FormControl, Paper } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import MoreVertIcon from "@mui/icons-material/MoreVert";  
import QrCode2Icon from "@mui/icons-material/QrCode2";
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import AssignmentIcon from '@mui/icons-material/Assignment';
import LastPageIcon from "@mui/icons-material/LastPage";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import UndoIcon from "@mui/icons-material/Undo";
import SearchIcon from "@mui/icons-material/Search";
import { clientDucStatusOptions } from "./certConst";
import { BASE_URL, DOMAIN } from "./../../global";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import "./certificateList.css";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ViewUploadedTrainingFile from "./viewUploadedTrainingFile";
import { SopViewFile, TrainingAndMasterViewFile } from "./sopandtraingbtn";
import ListWrap from "../commonComponent/listWrap";
import MultipleCertificatePrintButton from "./multipleCertificatePrintButton";
import { getFormattedDates } from "../../utils/utils";
import MultipleCertificateCopyUrl from "./multipleCertificateCopyUrl";
import { handleWithdrawApproval } from "./handleWithdrawApproval";
import { mulipleApproveCertificates } from "./mulipleApproveCertificates";
import EditNoteIcon from "@mui/icons-material/EditNote";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { notifiy } from "../notification/Notification";
import { locationOptions } from "../../constants/srfConstants";
import { paginationrowsPerPageOptions } from "../../constants/commonConst";
import ExcelDownload from "../../utils/components/excelDownload";
import { DateisValid, parseRange } from "../../utils/components/accuracyandlcandrangesshow";

const notNeededColumn = ["id", "status",];

/* 1. "Submitted for Approval" is derived from `status = 3` on certificatesView,
 * 2. "Certificate Status" (Draft(Cal. Pending) / Calibrated / Amendment) and
 *    "Approval Status" (Pending Approval / Approved / Rejected / Sent 
 *    approvedby, rejectedBy, status). They do not change any existing
 *    filter, permission, or approval logic - they only affect what's shown
 *    in the two new chip columns.
 *  */

const CERT_STATUS_SENT_FOR_APPROVAL = 3;

const APPROVAL_QUEUE_BASE_SQL = `(calibratedby IS NOT NULL AND approvedby IS NULL AND totalReadings IS NOT NULL AND totalReadings > 0)`;
const APPROVED_SQL = `(calibratedby IS NOT NULL AND approvedby IS NOT NULL AND totalReadings IS NOT NULL AND totalReadings > 0)`;
const AMENDMENTS_SQL = `(revisionNumber > 0)`;
const UNAPPROVED_SQL = `(${APPROVAL_QUEUE_BASE_SQL} AND rejectedBy IS NULL)`;
const REJECTED_SQL = `(${APPROVAL_QUEUE_BASE_SQL} AND rejectedBy IS NOT NULL)`;
const SENT_FOR_APPROVAL_SQL = `(status = ${CERT_STATUS_SENT_FOR_APPROVAL} AND approvedBy IS NULL)`;

// Top-level tabs - badge counts come from the single combined counts query
const MAIN_TABS = [
  { key: "all", label: "All Certificates" },
  { key: "approvalQueue", label: "Approval Queue" },
  { key: "approved", label: "Approved" },
  { key: "amendments", label: "Amendments" },
];

// Sub-tabs, only shown under "Approval Queue"
const SUB_TABS = [
  { key: "all", label: "All" },
  { key: "submittedForApproval", label: "Submitted for Approval" },
  { key: "unapproved", label: "Pending Approval" },
  { key: "rejected", label: "Rejected" },
];

const STATUS_CHIP_STYLES = {
  Calibrated: { color: "success" },
  "Cal. Pending": { color: "error" },
  Amendment: { color: "secondary" },
  Approved: { color: "info" },
  "Pending Approval": { color: "warning" },
  Rejected: { color: "error" },
  "Submitted for Approval": { color: "secondary" },
};

// Derived, read-only labels for the two new chip columns (see note above).
const getCertificateStatusLabel = (row) => {
  if (row?.revisionNumber > 0) return "Amendment";
  if (row?.totalReadings != null && row?.totalReadings > 0) return "Calibrated";
  return "Cal. Pending";
};

const getApprovalStatusLabel = (row) => {
  if (row?.approvedby != null && row?.approvedby !== "") return "Approved";
  if (row?.rejectedBy != null && row?.rejectedBy !== "") return "Rejected";
  if (row?.status == CERT_STATUS_SENT_FOR_APPROVAL) return "Submitted for Approval";
  if (row?.calibratedby != null && row?.totalReadings != null && row?.totalReadings > 0) return "Pending Approval";
  return null;
};

const toolbarDateFieldSx = {
  flex: {
    xs: "1 1 100%",
    sm: "1 1 calc(50% - 12px)",
    md: "1 1 150px",
    lg: "1 1 175px",
    xl: "1 1 200px",
  },
  minWidth: { xs: "100%", sm: 130, md: 110, lg: 130 },
  maxWidth: { xs: "100%", sm: "none", md: 220, lg: 240 },
};

const toolbarActionGroupSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1.5,
  alignItems: "stretch",
  width: { xs: "100%", lg: "auto" },
};

const toolbarButtonSx = {
  height: 31,
  px: 2,
  whiteSpace: "nowrap",
};

export default function CertificateList(props) {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const urlPath = useLocation();
  const printComponentRef = React.useRef();
  const [page, setPage] = React.useState(() => Number(sessionStorage.getItem("ListPageCount") || 0));
  const [rowsPerPage, setRowsPerPage] = React.useState(() => Number(sessionStorage.getItem("ListRowsPerPage") || 30));
  const [certificate, setCertificate] = useState([]);
  const [userList, setUserList] = useState([]);
  const [allcertificateIds, setAllCertificateIds] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(sessionStorage.getItem("groupeSelectedFilters") || "");
  const [selectedLocation, setSelectedLocation] = useState(() => {
  const storedLocation = sessionStorage.getItem("selectedLocation");
  return storedLocation ? JSON.parse(storedLocation) : []});
  const [selectedClientDucStatus, setSelectedClientDucStatus] = useState(() => {
    const storedStatus = sessionStorage.getItem("selectedClientDucStatus");
    return storedStatus ? JSON.parse(storedStatus) : []
  });
  const [progressModalOpen, setProgressModalOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [inwardStatusRow, setInwardStatusRow] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = React.useState(false);
  const [enableDisable, setEnableData] = React.useState("");
  const [searchBy, setSearchBy] = React.useState(sessionStorage.getItem("searchBy") || "DUC ID");
  const [searchKey, setSearchKey] = React.useState(sessionStorage.getItem("searchKey") || "");
  const _clientId = localStorage.getItem("clientId");
  const _companyHierarchy = localStorage.getItem("companyHierarchy");
  const [editAccess, setEditAccess] = React.useState(localStorage.getItem("editAccess")?.split(",").map(Number));
  const [userType, setUserType] = React.useState(localStorage.getItem("type"));
  const [userId, setUserId] = React.useState(localStorage.getItem("id"));
  const [branch, setBranch] = React.useState(localStorage.getItem("branch"));
  const validCompanies = localStorage.getItem("validClients");
  const [stikerLoader, setStikerLoader] = React.useState(false);
  const [settingList, setSettingList] = React.useState({});
  const [searched, setSearched] = React.useState(false);
  const [from, setFrom] = useState(sessionStorage.getItem("dateFrom") || "");
  const [to, setTo] = useState(sessionStorage.getItem("dateTo") || "");
  const [result, setResult] = React.useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const disciplineID = localStorage.getItem("disciplineID");
  const navigate = useNavigate();
  const [multiPrintRow, setmultiPrintRow] = useState(false);
  const [multiCopyURL, setmultiCopyURL] = useState(false);
  const [excelData, setExcelData] = React.useState([]);
  const [downloadCounts, setDownloadCounts] = useState({});

  // Tab state - mirrors DatasheetList's main tab / sub-tab pattern
  const [mainTab, setMainTab] = useState(() => sessionStorage.getItem("mainTab") || "all");
  const [subTab, setSubTab] = useState(() => sessionStorage.getItem("subTab") || "all");
  // Counts for every tab / sub-tab, fetched in a SINGLE query (see
  // buildCountsQuery) instead of one COUNT() call per badge.
  const [counts, setCounts] = useState({
    allCount: 0,
    approvedCount: 0,
    amendmentsCount: 0,
    approvalQueueCount: 0,
    unapprovedCount: 0,
    rejectedCount: 0,
    submittedForApprovalCount: 0,
  });
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuTarget, setRowMenuTarget] = useState(null);  
  const [loading, setLoading] = React.useState(false);

  const [selectedSRFMultiple, setselectedSRfMultiple] = useState(null);
  const [multipleSRF, setmultipleSRF] = useState(false);
  const [filterDisciplines, setFilterDisciplines] = React.useState([]);  
  const [selectedDisciplineIds, setSelectedDisciplineIds] = useState(sessionStorage.getItem("selectedDisciplineIds") || []);
  
  const handleFilterOpen = () => setFilterModalOpen(true);
  const handleFilterClose = () => setFilterModalOpen(false);
  const handleCloseProgress = () => setProgressModalOpen(false);
  const handleOpenProgress = (row) => {
    setSelectedRow(row);
    setProgressModalOpen(true);
  };
const [modalOpen, setModalOpen] = useState(false);

  const openRowMenu = (event, row) => {
    setRowMenuAnchorEl(event.currentTarget);
    setRowMenuTarget(row);
  };
  const closeRowMenu = () => {
    setRowMenuAnchorEl(null);
    setRowMenuTarget(null);
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [muliplePDfOption, setmuliplePDfOption] = React.useState(true);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMultiplePdfClose = (option) => {
    setmuliplePDfOption(option)    
    setAnchorEl(null);
  };

  const qrModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "90%" : 600,
    maxWidth: isMobile ? "90%" : "none",
    height: 300,
    bgcolor: "background.paper",
    border: "2px solid cadetblue",
    boxShadow: 24,
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem("currentPath", urlPath.pathname);
    sessionStorage.setItem("ListPageCount", newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    sessionStorage.setItem("ListRowsPerPage", event.target.value);
    sessionStorage.setItem("ListPageCount", 0);
  };

  const buildBaseWhere = () => {
    let whereConditions = "";

    // Handle multiple filter selections
    const statusFilterMap = {
      Unapproved: `(calibratedby IS NOT NULL AND approvedby IS NULL AND rejectedBy IS NULL AND totalReadings IS NOT NULL AND totalReadings > 0)`,
      Approved: `(calibratedby IS NOT NULL AND approvedby IS NOT NULL AND totalReadings IS NOT NULL AND totalReadings > 0)`,
      Rejected: `(calibratedby IS NOT NULL AND rejectedBy IS NOT NULL AND totalReadings IS NOT NULL AND totalReadings > 0)`,
      "Amendment Cert.": `(revisionNumber > 0)`,
    };

    if (selectedFilters && statusFilterMap[selectedFilters]) {
      whereConditions += ` WHERE ${statusFilterMap[selectedFilters]} `;
    }
    if (selectedLocation?.length > 0) {
      whereConditions += whereConditions.length > 0 ? ` AND location IN (${selectedLocation.join(",")}) ` : `WHERE location IN (${selectedLocation.join(",")})`;
    }
    if (selectedClientDucStatus?.length > 0) {
      whereConditions +=
        whereConditions.length > 0
          ? ` AND clientDucStatus IN (${selectedClientDucStatus.join(",")}) `
          : `WHERE clientDucStatus IN (${selectedClientDucStatus.join(",")})`;
    }
    if (searchBy !== "" && searchKey != "") {
      let colName = {
        Client: "companyName",
        "Instrument Name": "instrumentName",
        "DUC ID": "DUCID",
        "Serial Number": "serialNumber",
        "SRF Number": "serviceReqNumber",
        "Job Number": "jobNumber",
        "Certificate Number": "certificateNumber",
        "DUC Location": "locationOfInstrument",
        "ULR No": "ULRNo",
        "Model": "model",
      }[searchBy];
      const searchCondition = `LOWER(${colName}) like LOWER('%${searchKey}%')`;
      whereConditions = whereConditions 
        ? `${whereConditions} AND ${searchCondition}` 
        : `WHERE ${searchCondition}`;
    }

    // Add status condition
      if (whereConditions === "") {
        whereConditions += ` WHERE status != -1`;
    } else {
      whereConditions += ` and status != -1 `;
    }

    if (from && to) {
      const { fromDate, toDate } = getFormattedDates(from, to);
      whereConditions += ` and approvedDate between '${fromDate}' and '${toDate}' `;
    }
    if(props.isCount == true){
      whereConditions +=
      whereConditions.length > 0
        ? ` and calibratedby is NOT NULL and approvedby is NULL and discID IN (${disciplineID}) `
        : `where calibratedby is NOT NULL and approvedby is NULL and discID IN (${disciplineID}) `;
    }
    if (props.today) {
      const today = new Date().toISOString().slice(0, 10);
      whereConditions += ` AND DATE(lastmodified) = '${today}'`;
    }
    if ((userType == "2" || userType == "8") && props.rolesInfo?.currentRole?.id == 2 && disciplineID !="null") {
      whereConditions +=
        whereConditions.length > 0
          ? ` and (discID IN (${disciplineID}) `
          : `where (discID IN (${disciplineID}) `;
      //site incharge show only user assign companies
      if (userType == "8" && validCompanies?.length > 0) {
        whereConditions += ` or cust.id in (${validCompanies}))`;
      } else {
        whereConditions += `)`;
      }
    }
    if ((userType == "2" || userType == "8") && disciplineID=="null") {
      whereConditions +=
        whereConditions.length > 0
          ? ` and (assignedUser=${userId}`
          : `where (assignedUser=${userId}`;
      whereConditions += ` or createdby=${userId}`;
    
      //site incharge show only user assign companies
      if (userType == "8" && validCompanies?.length > 0) {
        whereConditions += ` or custid in (${validCompanies}))`;
      } else {
        whereConditions += `)`;
      }
    }
    if (userType === "3") {
      whereConditions += whereConditions.length > 0
        ? ` and (clientId=${_clientId}) and approvedby IS NOT NULL and approvedby != ''`
        : `where (clientId=${_clientId}) and approvedby IS NOT NULL and approvedby != ''`;
    }
    if (userType === "3" && _companyHierarchy && _companyHierarchy !== "null") {
      const arr = _companyHierarchy.split(",");
      const mid = Math.floor(arr.length / 2);

      if (arr.length >= 2 && arr.length % 2 === 0) {
        const pairs = arr.slice(0, mid).map((prefix, i) => {
          const suffix = arr[mid + i];
          return `(companyHierarchy='${prefix},${suffix}')`;
        });

        if (pairs?.length) {
          whereConditions += whereConditions.length
            ? ` and (${pairs.join(" or ")})`
            : ` where (${pairs.join(" or ")})`;
        }
      }
    }
    if (selectedDisciplineIds?.length > 0) {
      whereConditions += whereConditions.length > 0
        ? ` and discID in (${selectedDisciplineIds})`
        : ` where discID in (${selectedDisciplineIds})`;
    }

    if (settingList["hideSRF"] === "true") {
      whereConditions +=
        whereConditions.length > 0
          ? ` AND (hideSrfUserIds IS NULL OR FIND_IN_SET(${userId}, hideSrfUserIds) = 0)`
          : ` AND (hideSrfUserIds IS NULL OR FIND_IN_SET(${userId}, hideSrfUserIds) = 0)`;
    }

    whereConditions +=
      (userType == 4 || userType == 5) && branch && branch.length
        ? ` AND branch in (${branch})`
        : "";

    return whereConditions;
  };

  // ROWS / EXCEL queries only. The combined counts query needs every tab's
  const tabCondition = useMemo(() => {
    if (mainTab === "approved") return APPROVED_SQL;
    if (mainTab === "amendments") return AMENDMENTS_SQL;
    if (mainTab === "approvalQueue") {
      switch (subTab) {
        case "unapproved":
          return UNAPPROVED_SQL;
        case "rejected":
          return REJECTED_SQL;
        case "submittedForApproval":
          return SENT_FOR_APPROVAL_SQL;
        default:
          return APPROVAL_QUEUE_BASE_SQL;
      }
    }
    return null; 
  }, [mainTab, subTab]);

  const appendCondition = (whereConditions, condition) => {
    if (!condition) return whereConditions;
    return whereConditions.length > 0 ? `${whereConditions} AND ${condition}` : `WHERE ${condition}`;
  };

  const getQuery = (removePagination = false) => {
    let whereConditions = appendCondition(buildBaseWhere(), tabCondition);
    let pagination_settings = "";

    if (!removePagination && rowsPerPage != -1) {
      pagination_settings = `limit ${rowsPerPage} offset ${rowsPerPage * Number(page)}`;
    }

    return {
      query: `SELECT * FROM certificatesView ${whereConditions} order by id DESC ${pagination_settings}`,
    };
  };

  // Single aggregate query: gives the row-count for every tab AND sub-tab
  // COUNT() call per tab badge - the same
  const buildCountsQuery = () => {
    const whereConditions = buildBaseWhere();
    const query = `SELECT
        COUNT(*) AS allCount,
        SUM(CASE WHEN ${APPROVED_SQL} THEN 1 ELSE 0 END) AS approvedCount,
        SUM(CASE WHEN ${AMENDMENTS_SQL} THEN 1 ELSE 0 END) AS amendmentsCount,
        SUM(CASE WHEN ${APPROVAL_QUEUE_BASE_SQL} THEN 1 ELSE 0 END) AS approvalQueueCount,
        SUM(CASE WHEN ${UNAPPROVED_SQL} THEN 1 ELSE 0 END) AS unapprovedCount,
        SUM(CASE WHEN ${REJECTED_SQL} THEN 1 ELSE 0 END) AS rejectedCount,
        SUM(CASE WHEN ${SENT_FOR_APPROVAL_SQL} THEN 1 ELSE 0 END) AS submittedForApprovalCount FROM certificatesView ${whereConditions}`;
    return { query: query.replace(/\s+/g, " ").trim() };
  };

  const totalRowsForActiveTab = useMemo(() => {
    if (mainTab === "approved") return counts.approvedCount || 0;
    if (mainTab === "amendments") return counts.amendmentsCount || 0;
    if (mainTab === "approvalQueue") {
      switch (subTab) {
        case "unapproved":
          return counts.unapprovedCount || 0;
        case "rejected":
          return counts.rejectedCount || 0;
        case "submittedForApproval":
          return counts.submittedForApprovalCount || 0;
        default:
          return counts.approvalQueueCount || 0;
      }
    }
    return counts.allCount || 0;
  }, [mainTab, subTab, counts]);

  // api calls
  function fetchCounts() {
    return axiosWithToken
      .post(BASE_URL + `dynamic`, buildCountsQuery())
      .then((res) => {
        const row = res.data?.[0] || {};
        setCounts({
          allCount: Number(row.allCount || 0),
          approvedCount: Number(row.approvedCount || 0),
          amendmentsCount: Number(row.amendmentsCount || 0),
          approvalQueueCount: Number(row.approvalQueueCount || 0),
          unapprovedCount: Number(row.unapprovedCount || 0),
          rejectedCount: Number(row.rejectedCount || 0),
          submittedForApprovalCount: Number(row.submittedForApprovalCount || 0),
        });
      })
      .catch((err) => console.error("counts fetching error: ", err));
  }

  function fetchCertificates() {
    let data = getQuery();
    return axiosWithToken
      .post(BASE_URL + `dynamic`, data)
      .then((res) => {
        setCertificate([...res.data]);
        let data = res.data;
        const AllIds = data.map((e) => e.id);
        setAllCertificateIds(AllIds);
         fetchDownloadCounts(AllIds);
      })
      .catch((err) => console.log("srfs data fetching error: ", err));
  }

  // Single entry point that loads the table rows + every tab/sub-tab count
  // table always refresh together in exactly two round trips (rows +
  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchCounts(), fetchCertificates()]);
    } finally {
      setLoading(false);
    }
  };

  function fetchAllDataForExcel() {
    const data = getQuery(true);

    axiosWithToken
      .post(BASE_URL + `dynamic`, data)
      .then((res) => {
        const processed = res.data.map((item) => {
          const leastcount = parseRange(item.leastcount);
          const ranges = parseRange(item.ranges);
          const companyHierarchyCell = item.companyHierarchy?.split(",") || [];
          // return only required excel fields
          return {
            companyName: item.companyName || "",
            serviceReqNumber: item.serviceReqNumber || "",
            certificateNumber: item.certificateNumber || "",
            ULRNo: item.ULRNo || "",
            jobNumber: item.jobNumber || "",
            // Division & Department columns
            ...(settingList["companyHierarchy"] && {
              division: companyHierarchyCell[0]?.trim() || "",
              department: companyHierarchyCell[1]?.trim() || "",
            }),
            instrumentName: item.instrumentName || "",
            ducLocation: item.locationOfInstrument || "",
            ranges,
            leastcount,
            DUCID: item.DUCID || "",
            serialNumber: item.serialNumber || "",
            calibrationDate: item.calibrationDate ? moment(item.calibrationDate).format("DD-MM-YYYY") : "",
            serialNumber: item.serialNumber || "",
            DueDate: item?.calFrequency == "Not Required"
              ? "-" : item?.calFrequency == "Customer Defined"
                ? "Customer Defined"
                : item?.calFrequency == "NA" ? "NA"
                  : item?.nextDueDate ? moment(item.nextDueDate).format("DD-MM-YYYY") : "",
            //  CalibratedBy: userList?.find((user) => user.id == item.calibratedby)?.userName || ""
          };
        });
        setExcelData(processed);
      })
      .catch(() => console.log("Failed to fetch data for Excel export")
      );
  }

  function fetchDisciplinesForSRF() {
    axiosWithToken
      .post(BASE_URL + `dynamic`, {
        query: `SELECT id, name FROM discipline ORDER BY id;`,
      })
      .then((res) => setFilterDisciplines(res.data))
      .catch(() => setFilterDisciplines([]));
  }

  const TablePaginationActions = (props) => {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
      onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
      onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
      onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
      <Box sx={{ flexShrink: 0, ml: 2.5 }}>
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={page === 0}
          aria-label="first page"
        >
          {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>
        <IconButton
          onClick={handleBackButtonClick}
          disabled={page === 0}
          aria-label="previous page"
        >
          {theme.direction === "rtl" ? (
            <KeyboardArrowRight />
          ) : (
            <KeyboardArrowLeft />
          )}
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          {theme.direction === "rtl" ? (
            <KeyboardArrowLeft />
          ) : (
            <KeyboardArrowRight />
          )}
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </Box>
    );
  };

  const storedSearchBy = sessionStorage.getItem("searched");
  useEffect(() => {
    if(storedSearchBy){
      setSearched(true);
    }
  }, [storedSearchBy]);

  const search = () => {
    sessionStorage.setItem("searchBy", searchBy);
    sessionStorage.setItem("searchKey", searchKey);
    sessionStorage.setItem("searched", "true");
    sessionStorage.setItem("currentPath", urlPath.pathname);
    sessionStorage.setItem("ListPageCount", 0);
    sessionStorage.setItem("groupeSelectedFilters", selectedFilters);
    sessionStorage.setItem("selectedDisciplineIds", selectedDisciplineIds);
    sessionStorage.setItem("selectedLocation", JSON.stringify(selectedLocation));
    sessionStorage.setItem("selectedClientDucStatus", JSON.stringify(selectedClientDucStatus));    
    if (from) sessionStorage.setItem("dateFrom", from);
    else sessionStorage.removeItem("dateFrom");
    if (to) sessionStorage.setItem("dateTo", to);
    else sessionStorage.removeItem("dateTo");
    setPage(0);
    setSearched(true);
    loadData();
    fetchAllDataForExcel();
  };

  // and whenever the user switches tabs, so a stale search/filter doesn't
  // silently keep filtering the newly selected tab's results.
  const resetSearchState = () => {
    sessionStorage.removeItem("searchBy");
    sessionStorage.removeItem("searchKey");
    sessionStorage.removeItem("searched");
    sessionStorage.removeItem("currentPath");
    sessionStorage.removeItem("ListPageCount");
    sessionStorage.removeItem("ListRowsPerPage");
    sessionStorage.removeItem("groupeSelectedFilters");
    sessionStorage.removeItem("selectedDisciplineIds");
    sessionStorage.removeItem("selectedLocation");
    sessionStorage.removeItem("selectedClientDucStatus");
    sessionStorage.removeItem("dateFrom");
    sessionStorage.removeItem("dateTo");
    setSearchBy("DUC ID");
    setSearchKey("");
    setFrom("");
    setTo("");
    setSelectedFilters("");
    setSelectedDisciplineIds([]);
    setSelectedLocation([]);
    setSelectedClientDucStatus([]);
    setSearched(false);
  };

  const clearSearch = () => {
    sessionStorage.removeItem("currentPath");
    sessionStorage.removeItem("ListPageCount");
    sessionStorage.removeItem("ListRowsPerPage");
    setPage(0);
    resetSearchState();
    navigate("/certificate");
  };

  // Tab handlers - mirror DatasheetList: switching tabs resets page + any
  // active search/filter, then the consolidated loading effect (below)
  // refetches rows + counts for the newly selected tab.
  const handleMainTabChange = (event, newTab) => {
    setMainTab(newTab);
    setSubTab("all");
    setPage(0);
    resetSearchState();
    sessionStorage.setItem("mainTab", newTab);
    sessionStorage.setItem("subTab", "all");
  };

  const handleSubTabChange = (newSubTab) => {
    setSubTab(newSubTab);
    setPage(0);
    resetSearchState();
    sessionStorage.setItem("subTab", newSubTab);
  };

  const processEnableDisable = (enableDisable) => {
    if (enableDisable.length > 0) {
      return enableDisable
        .map(function (item) {
          if (item) {
            let [id, closed] = item.split(",");
            return closed === "true" ? id : null;
          }
        })
        .filter(function (item) {
          return item !== undefined && item !== null;
        });
    }
    return [];
  };

  function fetchSettings() {
    axiosWithToken
      .get(BASE_URL + `settings?_where=(status,eq,1)`)
      .then((res) => {
         let settings = res.data;
        let _settingList = {};
        settings.forEach((setting) => {
          _settingList[setting.keyName] = setting.value;
        });
        setSettingList(_settingList);
      })
      .catch((err) => console.error("Error fetching setting:", err));
  }

  const getUsersList = () => {
    axiosWithToken
      .get(BASE_URL + `users?_where=(status,eq,1)`)
      .then((res) => {
        setUserList(res.data);
      })
      .catch((error) => {
        if (error.message !== "request_aborted") {
          toast.error("Something Went Wrong!");
        }
      });
  };

  const ShowRejectModal = () => {
    const [amendmentReason, setAmendmentReason] = useState("");
    return (
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        size="md"
      >
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 500, bgcolor: "background.paper", border: "2px solid cadetblue", padding: "20px", textAlign: "center", borderRadius: "5px", }}>
          <TextField
            id="outlined-basic"
            label="Reason For Amendment"
            value={amendmentReason}
            multiline
            maxRows={5}
            rows={5}
            size="small"
            fullWidth
            variant="outlined"
            style={{ marginBottom: "5px" }}
            onChange={(e) => setAmendmentReason(e.target.value)}
          />
          <Tooltip title="Send Rejection Reason" placement="top-start">
            <Button
              variant="contained"
              color="success"
              size="small"
              sx={{ m: 2 }}
              onClick={() => {
                handleRequestAmendment(selectedRow, amendmentReason);
              }}
              disabled={amendmentReason === ""}
            >
              Send
            </Button>
          </Tooltip>
        </Box>
      </Modal>
    );
  };

const handleRequestAmendment = (row, amendmentReason) => {
  axiosWithToken
    .post(`${BASE_URL}dynamic`, {
      query: `UPDATE certificates SET isAmendment=1, status=1, approvedBy=NULL, approvedDate=NULL, reviewedBy=NULL, reviewedDate=NULL, signedBy=NULL, signedDate=NULL, rejectedBy=NULL, witnessBy=NULL, witnessDate=NULL WHERE id=${row.id}`
    })
   .then(() => {
        toast.success("Request Amendment successful!");
     const notification = {
       _from: localStorage.getItem("id"),
       _to: row?.calibratedby,
       body: `${row?.instrumentName} (${row?.DUCID}) \nPlease amend the returned certificate as per the required changes..\n Reason : ${amendmentReason}`,
       type: 1,
     };
     notifiy(notification, () => {
       console.log(`Notified of certificate for Amendment`);
     });
        setTimeout(() => window.location.reload(), 500); // reload after 1s
      })

    .catch((error) => {
      if (error.message !== "request_aborted") {
        toast.error("Something went wrong!");
      }
    });
};
  const fetchDownloadCounts = (ids) => {
    if (!ids.length) return;
    axiosWithToken.post(BASE_URL + "dynamic", {
      query: `SELECT identifier, COUNT(*) downloadCount FROM audit_log WHERE type='certificates' AND action='download' AND identifier IN (${ids.join(",")}) GROUP BY identifier`
    }).then(res => {
      const obj = {};
      res.data.forEach(d => obj[d.identifier] = d.downloadCount);
      setDownloadCounts(obj);
    });
  };

  useEffect(() => {
    setResult(processEnableDisable(enableDisable));
  }, [enableDisable]);

  useEffect(() => {
    fetchSettings();
    getUsersList();
    fetchDisciplinesForSRF();
  }, []);

  useEffect(() => {
    loadData();
  }, [page, rowsPerPage, mainTab, subTab, disciplineID, settingList["hideSRF"]]);

  useEffect(() => {
    if (searched == false) {
      loadData();
      fetchAllDataForExcel();
    }
    if (searched) {
      setPage(sessionStorage.getItem("ListPageCount") || 0);
    }
  }, [searched]);

  useEffect(() => {
    if (props.isCount === true) {
      loadData();
    }
  }, [props.isCount]);

  useEffect(() => {
    if (selectedSRFMultiple) {
      setSearchBy("SRF Number");
      setSearchKey(selectedSRFMultiple);
      setmultipleSRF(true);     
    }
  }, [selectedSRFMultiple]); 

  useEffect(() => {
    if (multipleSRF) {
      loadData();
      setmultipleSRF(false); 
    }
  }, [multipleSRF,selectedSRFMultiple]);

  const renderProgressModal = () => {
    if (!selectedRow) return null;
    return (
      <Modal
        open={progressModalOpen}
        onClose={handleCloseProgress}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        size="md"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800,
            height: "70%",
            backgroundColor: "white",
            padding: 3,
            borderRadius: 2,
          }}
        >
          <Box sx={{display:"flex", justifyContent:"space-between", mb:1}}>
          <Typography variant="h6">Progress Details</Typography>

          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={handleCloseProgress}
          >
            X
          </Button>
          </Box>

          {settingList["activeRole1"] != "false" && (
            <Typography
              sx={{ color: "black", backgroundColor: "#22c9b0", padding: 1 }}
              variant="body1"
            >
              Calibrated :{" "}
              {
                userList.find((user) => user.id == selectedRow.calibratedby)
                  ? `${userList.find((user) => user.id == selectedRow.calibratedby)?.userName} ${selectedRow?.calibrateddesignation ? `(${selectedRow?.calibrateddesignation})` : ''}`
                  : "Pending..."
              }
            </Typography>
          )}
          {settingList["activeRole2"] != "false" && (
            <Typography
              sx={{
                color: "black",
                backgroundColor: "rgb(160 160 247)",
                padding: 1,
              }}
              variant="body1"
            >
              Certificate Approval :{" "}
              {
                userList.find((user) => user.id == selectedRow.approvedby)
                  ? `${userList.find((user) => user.id == selectedRow.approvedby)?.userName} ${selectedRow?.approveddesignation ? `(${selectedRow?.approveddesignation})` : ''}`
                  : "Pending..."
              }
            </Typography>
          )}
          {settingList["activeRole3"] != "false" && (
            <Typography
              sx={{
                color: "black",
                backgroundColor: "rgb(243 146 243)",
                padding: 1,
              }}
              variant="body1"
            >
              Customer Approval (Reviewed) :{" "}
              {
                userList.find((user) => user.id == selectedRow.reviewedBy)
                  ?.userName || "Pending..."
              }              
            </Typography>
          )}
          {settingList["activeRole4"] != "false" && (
            <Typography
              sx={{
                color: "black",
                backgroundColor: "rgb(181 247 204)",
                padding: 1,
              }}
              variant="body1"
            >
              Customer Approval (Signed) :{" "}
              {
                userList.find((user) => user.id == selectedRow.signedBy)
                  ?.userName || "Pending..."
              }
            </Typography>
          )}
          {settingList["activeRole5"] != "false" && (
            <Typography
              sx={{ color: "black", backgroundColor: "#f59b79", padding: 1 }}
              variant="body1"
            >
              Customer Approval (Witnessed) :{" "}
              {
                userList.find((user) => user.id == selectedRow.witnessBy)
                  ?.userName || "Pending..."
              } 
            </Typography>
          )}
          <br />

          {settingList["SOPUpload"] === "true" && (
            <SopViewFile instrumentId={selectedRow.instrumentId} />
          )}
          {settingList["TrainingAndMasterCertificate"] === "true" && (
            <>
              <TrainingAndMasterViewFile
                calibratedbyID={selectedRow.calibratedby}
              />
              <ViewUploadedTrainingFile certificateId={selectedRow.id} />
            </>
          )}
        </Box>
      </Modal>
    );
  };

  const handleBulkWithdraw = () => {
    const idClientPairs = certificate
      ?.filter((row) => selectedRows?.includes(row.id))
      ?.map((row) => ({
        id: row.id,
        clientId: row.clientId,
      }));
    handleWithdrawApproval(idClientPairs);
  };
  const [actionValue, setActionValue] = React.useState("");
  const actionOptions = [
    ...(props.rolesInfo?.currentRole?.id == 2 ? [
      { value: "approve", label: "Approve", icon: <CheckCircleIcon fontSize="small" color="success" /> },
      { value: "withdraw", label: "Withdraw Approval", icon: <UndoIcon fontSize="small" /> },
    ] : []),
    ...(userType != 3 ? [{ value: "print-sticker", label: "Print Sticker", icon: <QrCode2Icon fontSize="small" /> }] : []),
    { value: "multiple-pdf", label: "Certificate PDF", icon: <PictureAsPdfIcon fontSize="small" color="success" /> },
  ];

  const handleContinue = () => {
    if (actionValue === "approve") {
      const selectedCertificates = certificate?.filter((cert) => selectedRows.includes(cert.id)) || [];

      const missingMasters = selectedCertificates.filter(cert => !cert.additionalStandardIds?.length);
      // missing certificateNumber
      const misscertificateNumber = selectedCertificates.filter(cert => !cert.certificateNumber);
      // Continue only with valid certificates
      const validCertificates = selectedCertificates.filter(cert => cert.additionalStandardIds?.length && cert.certificateNumber);

      if (missingMasters.length > 0) {
        const message = `Compliance Status is failed.\nMaster Details Not Found for the following:\n${missingMasters
          .map(cert => `Instrument Name: ${cert.instrumentName}, DUCID: ${cert.DUCID}, Job NO: ${cert.jobNumber}`)
          .join("\n")}`;
        window.alert(message);
      }
      if (misscertificateNumber.length > 0) {
        const message = `Certificate Number Not Found for the following:\n${misscertificateNumber
          .map(cert => `Instrument Name: ${cert.instrumentName}, DUCID: ${cert.DUCID}, Job NO: ${cert.jobNumber}`)
          .join("\n")}`;
        window.alert(message);
      }
      const noncomplianceStatusCerts = validCertificates?.filter((cert) => cert.complianceStatus != 1);
      const complianceStatusCerts = validCertificates
          ?.filter((cert) => cert.complianceStatus == 1)
          ?.map((cert) => cert.id) || [];
      const message =
        noncomplianceStatusCerts.length > 0
          ? `Compliance Status is failed for the following:\n${noncomplianceStatusCerts
            .map(
              (cert) =>
                `Instrument Name: ${cert.instrumentName}, DUCID: ${cert.DUCID}, Job NO: ${cert.jobNumber}`
            )
            .join("\n")}`
          : "";

      // Concatenating compliantand non-compliant certificates' IDs
      let allCertsToApprove = [
        ...(noncomplianceStatusCerts?.map((cert) => cert.id) ||
          []),
        ...complianceStatusCerts,
      ];

      // show alert Compliance Status failed
      if (
        settingList["Compliance Status Alert"]?.toLowerCase() ===
        "true" &&
        noncomplianceStatusCerts.length > 0
      ) {
        window.alert(message);
      }

      if (
        editAccess?.includes(7) &&
        allCertsToApprove.length > 0
      ) {
        mulipleApproveCertificates(
          "approve",
          allCertsToApprove,
          settingList
        );
      } else {
        if (complianceStatusCerts?.length > 0) {
          mulipleApproveCertificates(
            "approve",
            complianceStatusCerts,
            settingList
          );
        }
      }
    } else if (actionValue === "withdraw") {
      handleBulkWithdraw();
    } else if (actionValue === "print-sticker") {
      sessionStorage.setItem("multipleqrIDs", selectedRows.toString());
      navigate("/certificate/multiqr/");
    } else if (actionValue === "multiple-pdf") {
      const approvedIds = certificate
        ?.filter((row) => selectedRows.includes(row.id) && row.approvedby != null && row.approvedby !== "")
        ?.map((row) => row.id);
      if (!approvedIds?.length) {
        toast.error("No approved certificates found!");
        return;
      }
      const url = `/certificate/viewCertificate/${approvedIds.join(",")}`;
      navigate(url);
    }
    setActionValue("");
  };

  const mainTabsBlock = (
    <Tabs
      value={mainTab}
      onChange={handleMainTabChange}
      sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5, minHeight: 28 }}
      TabIndicatorProps={{ style: { height: 3 } }}
    >
      {MAIN_TABS
        .filter((t) => userType == 3 ? t.key === "all" : true)
        .map((t) => (
        <Tab
          key={t.key}
          value={t.key}
          sx={{ minHeight: 32, padding: "4px 12px", fontSize: "0.8rem" }}
          label={
            <span style={{ fontWeight: "bold" }}>
              {t.label}
              <b>
                ({t.key === "all"
                  ? counts.allCount
                  : t.key === "approvalQueue"
                  ? counts.approvalQueueCount
                  : t.key === "approved"
                  ? counts.approvedCount
                  : counts.amendmentsCount}
                )
              </b>
            </span>
          }
        />
      ))}
    </Tabs>
  );

  const subTabCountFor = (key) => {
    switch (key) {
      case "all":
        return counts.approvalQueueCount;
      case "unapproved":
        return counts.unapprovedCount;
      case "rejected":
        return counts.rejectedCount;
      case "submittedForApproval":
        return counts.submittedForApprovalCount;
      default:
        return 0;
    }
  };

  const subTabsBlock = mainTab === "approvalQueue" && (
    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
      {SUB_TABS.map((t) => (
        <Chip
          key={t.key}
          label={`${t.label} (${subTabCountFor(t.key)})`}
          clickable
          color={subTab === t.key ? "primary" : "default"}
          variant={subTab === t.key ? "filled" : "outlined"}
          onClick={() => handleSubTabChange(t.key)}
        />
      ))}
    </Stack>
  );

  const StatusChip = ({ status }) => {
    if (!status) return null;
    const style = STATUS_CHIP_STYLES[status] || { color: "default" };
    return <Chip size="small" label={status} color={style.color} />;
  };

  const filterSection = () => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-end", mb: 1 }}>
      {mainTab === "all" && (
        <>
          <Box sx={{ ...toolbarDateFieldSx, flex: "0 1 120px", minWidth: { xs: "100%", sm: 120 }, maxWidth: { xs: "100%", sm: 140 } }}>
            <TextField
              id="certificate_date_from"
              label="From Date"
              variant="outlined"
              type="date"
              className="Listinputcss"
              style={{ width: "100%" }}
              required={true}
              InputLabelProps={{ shrink: true }}
              size="small"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setSearched(false);
              }}
            />
          </Box>
          <Box sx={{ ...toolbarDateFieldSx, flex: "0 1 120px", minWidth: { xs: "100%", sm: 120 }, maxWidth: { xs: "100%", sm: 140 } }}>
            <TextField
              id="certificate_date_to"
              label="To Date"
              variant="outlined"
              type="date"
              className="Listinputcss"
              style={{ width: "100%" }}
              required={true}
              InputLabelProps={{ shrink: true }}
              size="small"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setSearched(false);
              }}
            />
          </Box>
        </>
      )}
      <Box sx={{ ...toolbarDateFieldSx, flex: "0 1 170px", minWidth: { xs: "100%", sm: 120 }, maxWidth: { xs: "100%", sm: 170 } }}>
        <Autocomplete
          sx={{m: 0, width: "100%",}}
          className="Listinputcss"
          size="small"
          ListboxProps={{
            sx: {
              "& .MuiAutocomplete-option": {
                fontSize: "14px !important",
                minHeight: "30px !important",
                padding: "3px 8px !important",
              },
            },
          }}
          value={searchBy}
          id="certificate-certificatelistsearchby"
          options={[
            ...(userType !== 3
              ? [{ key: "customerName", label: "Client" }]
              : []),
            { key: "instrumentName", label: "Instrument Name" },
            { key: "duc", label: "DUC ID" },
            { key: "serialNumber", label: "Serial Number" },
            { key: "srfNumber", label: "SRF Number" },
            { key: "model", label: "Model" },
            { key: "jobNumber", label: "Job Number" },
            { key: "certificateNumber", label: "Certificate Number" },
            { key: "locationOfInstrument", label: "DUC Location" },
            ...(userType !== 3
              ? [{ key: "ULRNo", label: "ULR No" }]
              : []),
          ]}
          renderInput={(params) => (
            <TextField {...params} label="Search By" />
          )}
          isOptionEqualToValue={(option, value) => option.label === value}
          onInputChange={(event, newInputValue) => {
            if (newInputValue === "") setSearchKey("");
            setSearchBy(newInputValue);
            setSearched(false);
          }}
        />
      </Box>
     <Box sx={{ ...toolbarDateFieldSx, flex: "0 1 170px", minWidth: { xs: "100%", sm: 120 }, maxWidth: { xs: "100%", sm: 170 } }}>
        <TextField
          style={{ width: "100%" }}
          className="Listinputcss"
          label={"Enter " + searchBy}
          size="small"
          inputProps={{
            autoComplete: 'off',
          }}
          variant="outlined"
          value={searchKey}
          onChange={(e) => {
            setSearchKey(e.target.value);
            setSearched(false);
          }}
        />
      </Box>
      <Box sx={{ ...toolbarDateFieldSx, flex: "0 1 150px", minWidth: { xs: "100%", sm: 120 }, maxWidth: { xs: "100%", sm: 150 } }}>
        <Autocomplete
          sx={{ m: 0, width: "100%" }}
          className="Listinputcss"
          size="small"
          multiple
          limitTags={1}
          ListboxProps={{
            sx: {
              "& .MuiAutocomplete-option": {
                fontSize: "14px !important",
                minHeight: "30px !important",
                padding: "3px 8px !important",
              },
            },
          }}          
          disableCloseOnSelect
          value={locationOptions.filter((option) => selectedLocation.includes(option.id))}
          id="datasheet_list_locationfilter"
          options={locationOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id == value.id}
          renderInput={(params) => (
            <TextField {...params} label="Calibration Location" />
          )}
          onChange={(event, newValue) => {
            setSelectedLocation(newValue.map((option) => option.id));
            setSearched(false);
          }}
        />
      </Box>
      {localStorage.getItem("modules")?.includes("9") && (
        <Box sx={toolbarDateFieldSx}>
          <Autocomplete
            sx={{ m: 0, width: "100%" }}
            className="Listinputcss"
            size="small"
            multiple
            limitTags={1}
            disableCloseOnSelect
            value={clientDucStatusOptions.filter((option) => selectedClientDucStatus.includes(option.id))}
            id="certificate_list_clientducstatusfilter"
            options={clientDucStatusOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id == value.id}
            renderInput={(params) => (
              <TextField {...params} label="Filter by DUC Status" />
            )}
            onChange={(event, newValue) => {
              setSelectedClientDucStatus(newValue.map((option) => option.id));
              setSearched(false);
            }}
          />
        </Box>
      )}
      {/* {userType != 3 && (
        <Box sx={toolbarFieldSx}>
          <Autocomplete
            sx={{
              m: 0,
              width: "100%",
            }}
            className="Listinputcss"
            size="small"
            value={selectedFilters}
            id="datasheet_list_statusfilter"
            options={["Unapproved", "Approved", "Rejected", "Amendment Cert."]}
            renderInput={(params) => (
              <TextField {...params} label="Certificate Status" />
            )}
            onChange={(event, newValue) => {
              setSelectedFilters(newValue);
              if (!["Approved", "Unapproved", "Rejected", "Amendment Cert."].includes(newValue)) {
                setSelectedDisciplineIds([]);
              }
              setSearched(false);
            }}
          />
        </Box>
      )} */}
      {userType != 3 && (
        <Box sx={{ ...toolbarDateFieldSx }}>
          <Autocomplete
            sx={{ m: 0, width: "100%", }}
            className="Listinputcss"
            size="small"
            multiple
            limitTags={1}
            disableCloseOnSelect
            ListboxProps={{
              sx: {
                "& .MuiAutocomplete-option": {
                  fontSize: "14px !important",
                  minHeight: "30px !important",
                  padding: "3px 8px !important",
                },
              },
            }}
            options={filterDisciplines.map((d) => ({ label: `${d.id}, ${d.name}`, value: d.id }))}
            value={filterDisciplines
              .filter((d) => selectedDisciplineIds.includes(d.id))
              .map((d) => ({ label: `${d.id}, ${d.name}`, value: d.id }))}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(event, newValue) => {
              setSelectedDisciplineIds(newValue.map((o) => o.value));
              setSearched(false);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0.5 }}/>
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Discipline" />
            )}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "flex-end", flex: { xs: "1 1 100%", sm: "0 0 auto" } }} >
        <Button
          variant="contained"
          size="small"
          color={searched ? "error" : "primary"}
          sx={{ height: 31, minWidth: 45, px: 1 }}
          onClick={() => searched ? clearSearch() : search()}
        >
          {searched ? <RestartAltIcon /> : <SearchIcon />}
        </Button>
      </Box>
    </Box>
  );

  const filterModal = () => {
    return (
      <Modal open={filterModalOpen} size="md">
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "calc(100% - 30%)",
            backgroundColor: "white",
            // border: "2px solid #000",
            boxShadow: 24,
            padding: "10px",
          }}
        >
          <Button
            variant="contained"
            size="small"
            color="error"
            sx={{ float: "right", minWidth: 20, width: 20, height: 20, padding: 0, fontSize: 10 }}
            onClick={handleFilterClose}
          >
            X
          </Button>
          {filterSection()}
        </Box>
      </Modal>
    );
  };

  const actionBarBlock = (
    <Box sx={{ display: "flex", pt: 0.5, borderTop: "1px solid", borderColor: "divider", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 1.5, mb: 1 }}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
        {((userType != 3) || (!isMobile && (editAccess?.includes(4) || editAccess?.includes(0)))) && (
          <>
            {userType != 3 && (
              <>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  size="small"
                  sx={{
                    ...toolbarButtonSx,
                    fontSize: 11,
                    padding: "10px"
                  }}
                  onClick={handleClick}
                >
                  Multiple PDF
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMultiplePdfClose}>
                  <MenuItem
                    onClick={() => {
                      handleMultiplePdfClose(true);
                      setmultiPrintRow(!multiPrintRow);
                    }}
                  >
                    Approved PDF
                  </MenuItem>
                  {userType != 3 && (
                    <MenuItem
                      onClick={() => {
                        handleMultiplePdfClose(false);
                        setmultiPrintRow(!multiPrintRow);
                      }}
                    >
                      Draft PDF
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
            {!isMobile && (editAccess?.includes(4) || editAccess?.includes(0)) && (
              <ExcelDownload
                finalData={excelData}
                notNeededColumn={notNeededColumn}
              />
            )}
          </>
        )}
      </Stack>

      {selectedRows && selectedRows.length ? (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180, height: 31 }}>
            <Select
              value={actionValue}
              onChange={(e) => setActionValue(e.target.value)}
              displayEmpty
              sx={{ height: 31 }}
              renderValue={(val) => {
                const opt = actionOptions.find((o) => o.value === val);
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {opt ? opt.icon : <RemoveCircleOutlineIcon fontSize="small" sx={{ opacity: 0.4 }} />}
                    <span>{opt ? opt.label : "Select action"}</span>
                  </Box>
                );
              }}
            >
              {actionOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <ListItemIcon>{opt.icon}</ListItemIcon>
                  <ListItemText>{opt.label}</ListItemText>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            disabled={!actionValue}
            startIcon={<PlayArrowIcon />}
            sx={{
              ...toolbarButtonSx,
              minWidth: {
                xs: "100%",
                sm: 180,
              },
            }}
            onClick={handleContinue}
          >
            Continue ({selectedRows.length})
          </Button>
        </Stack>
      ) : null}
    </Box>
  );

const handleSelectAllClick = (event) => {
  const allSelected =
    selectedRows.length ===
    certificate?.filter((row) => row.totalReadings !== null && row.totalReadings > 0).length;

  if (!allSelected || selectedRows.length === 0) {
    const newSelectedRows = certificate
      ?.filter((row) => row.totalReadings !== null && row.totalReadings > 0)
      ?.map((row) => row.id);
    setSelectedRows(newSelectedRows);
  } else {
    setSelectedRows([]);
  }
};

// Function to handle individual row selection
const handleRowClick = (event, id) => {
  const selectedIndex = selectedRows.indexOf(id);
  let newSelectedRows = [];

  if (selectedIndex === -1) {
    newSelectedRows = newSelectedRows.concat(selectedRows, id);
  } else if (selectedIndex === 0) {
    newSelectedRows = newSelectedRows.concat(selectedRows.slice(1));
  } else if (selectedIndex === selectedRows.length - 1) {
    newSelectedRows = newSelectedRows.concat(selectedRows.slice(0, -1));
  } else if (selectedIndex > 0) {
    newSelectedRows = newSelectedRows.concat(
      selectedRows.slice(0, selectedIndex),
      selectedRows.slice(selectedIndex + 1)
    );
  }
  setSelectedRows(newSelectedRows);
};
 const handleauditNavigate = (item) => {
    const auditIdArray = JSON.stringify([item.id]);
      // Store extra data in sessionStorage
      sessionStorage.setItem("auditExtraData", JSON.stringify({
        customerName: item.customerName,
        serviceReqNumber: item.serviceReqNumber,
        ducid: item.DUCID,
        serialNumber: item.serialNumber,
        certificatePage: item,
        userList,
        id:item.id,
        instrumentId: item.instrumentId
      }));
      
    window.open(
      `/#/auditlogview?id=${encodeURIComponent(auditIdArray)}`,
      "_blank"
    );
  };

  const colCount = settingList["companyHierarchy"] ? (userType !== "3" ? 21 : 20) : (userType !== "3" ? 19 : 18);

  return (
    <ListWrap>
      {filterModal()}
      {mainTabsBlock}
      {subTabsBlock}
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <Button
          style={{ display: isMobile ? "block" : "none" }}
          onClick={handleFilterOpen}
        >
          <FilterAltIcon />
        </Button>
      </div>
      {!isMobile && filterSection()}
      {actionBarBlock}
      {multiPrintRow && (
        <MultipleCertificatePrintButton
          multiPrintRow={multiPrintRow}
          setmultiPrintRow={setmultiPrintRow}
          setselectedSRfMultiple={setselectedSRfMultiple}
          clearSearch={clearSearch}
          setSearched={setSearched}
          muliplePDfOption={muliplePDfOption}
        />
      )}
      {multiCopyURL && (
        <MultipleCertificateCopyUrl
        multiCopyURL={multiCopyURL}
          setmultiCopyURL={setmultiCopyURL}
        />
      )}
      <Box sx={{ width: "100%", overflow: "hidden", display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table
            stickyHeader
            aria-label="sticky table"
            size="small"
            className="table-responsive"
            id="certificate-certificatelisttable"
            sx={{ borderCollapse: "collapse",
              "& .MuiTableCell-root": {
                padding: "6px 10px",
                fontSize: "0.75rem",
                lineHeight: 1.35,
              },
              "& .MuiTableCell-head": {
                fontSize: "0.75rem",
                fontWeight: 700,
              },
              "& .MuiChip-root": {
                height: 22,
                fontSize: "0.7rem",
              },
            }}>
            <TableHead>
              <TableRow>
                {!isMobile && (
                  <TableCell style={{padding:"5px"}}>
                    <Checkbox
                      indeterminate={
                        selectedRows.length > 0 &&
                        selectedRows.length < certificate.length
                      }
                      checked={selectedRows.length === certificate.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                )}
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap", padding: "5px" }}><b>Sr.No.</b></TableCell>
                <TableCell align="center" style={{ minWidth: 130, whiteSpace: "nowrap" }}><b>Client</b></TableCell>
                <TableCell align="center" style={{ minWidth: 120, whiteSpace: "nowrap" }}><b>Certificate Status</b></TableCell>
                <TableCell align="center" style={{ minWidth: 120, whiteSpace: "nowrap" }}><b>Approval Status</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Service Request Number</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Certificate Number</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>ULR No</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Job Number</b></TableCell>
                {settingList["companyHierarchy"] && <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Division</b></TableCell>}
                {settingList["companyHierarchy"] && <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Department</b></TableCell>}
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Instrument Name</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>DUC Location</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Range</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Least Count</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>DUC ID</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Serial Number</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Model</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Date of Calibration</b></TableCell>
                <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Due Date</b></TableCell>
                {userType !== "3" && <TableCell align="center" style={{ minWidth: 40, whiteSpace: "nowrap" }}><b>Calibrated By</b></TableCell>}
                <TableCell align="center"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colCount} align="center"><h6><b>Loading…</b></h6></TableCell>
                </TableRow>
              ) : certificate?.length > 0 ? (
                certificate.map((row, index) => {
                  let companyHierarchyCell =
                    row.companyHierarchy?.split(",") || [];
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                      {!isMobile ? (
                        <TableCell sx={{padding:"5px", textAlign:"center"}}>
                            <FormControlLabel
                              classes={{ justifyContent: "space-between", alignItems: "center"}}
                              sx={{ gap: 2, margin : 0 }}
                              control={
                                <Checkbox
                                  id="certificate-certificatelistselectcheckbox"
                                  checked={selectedRows.indexOf(row.id) !== -1}
                                  onChange={(event) =>
                                    handleRowClick(event, row.id)
                                  }
                                />
                              }
                              disabled={row.totalReadings == null || row.totalReadings == 0}
                              // label={matrixData.matrixName}
                            />

                        </TableCell>
                      ) : null}
                      <TableCell>
                        <Tooltip title="Certificate Download Count" placement="top">
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <div>{rowsPerPage * page + index + 1}</div>
                            <IconButton
                              onClick={handleClick}
                              size="small"
                              sx={{ ml: 2, mr: 1, height: "23px", width: "23px", marginLeft: "3px" }}
                              aria-haspopup="true"
                            >
                              <Badge
                                color="secondary"
                                badgeContent={`${downloadCounts[row.id] ?? 0}`}
                                sx={{
                                  "& .MuiBadge-badge": { top: -4, right: -4, },
                                }}
                              >
                                <DownloadIcon style={{ color: "#1976d2", fontSize: "20px" }} />
                              </Badge>
                            </IconButton>
                          </div>
                        </Tooltip>
                      </TableCell>
                      <TableCell  align="center" title={row.calibrationType == 1 ? "NABL" : row.calibrationType == 2 ? "IAS" : "NONANBL"}>
                        {row.companyName} <b>{row.uniCode ? `(${row.uniCode})` : ''}</b>
                      </TableCell>
                       <TableCell align="center"><StatusChip status={getCertificateStatusLabel(row)} /></TableCell>
                      <TableCell align="center">
                      <Tooltip
                          placement="top"
                        title={
                        userList?.find((user) => user.id == row.signedBy)?.userName
                          ? `Signed By ${userList.find((user) => user.id == row.signedBy)?.userName || ""}`
                          : userList?.find((user) => user.id == row.reviewedBy)?.userName
                          ? `Reviewed By ${userList.find((user) => user.id == row.reviewedBy)?.userName || ""}`
                          : userList?.find((user) => user.id == row.witnessBy)?.userName
                          ? `Witnessed By ${userList.find((user) => user.id == row.witnessBy)?.userName || ""}`
                          : userList?.find((user) => user.id == row.approvedby)?.userName
                          ? `Approved By ${userList.find((user) => user.id == row.approvedby)?.userName || ""}`
                          : userList?.find((user) => user.id == row.rejectedBy)?.userName
                          ? `Rejected By ${userList.find((user) => user.id == row.rejectedBy)?.userName || ""}`
                          : userList?.find((user) => user.id == row.calibratedby)?.userName
                          ? `Calibrated By ${userList.find((user) => user.id == row.calibratedby)?.userName || ""}`
                          : ""
                      }
                      >
                          <span style={{ display: "inline-block" }}>
                            <StatusChip status={getApprovalStatusLabel(row)} />
                          </span>
                        </Tooltip>
                      </TableCell>
                        <TableCell
                          // style={{
                          //   backgroundColor:
                          //     userList?.find((user) => user.id == row.signedBy)
                          //       ?.userName || ""
                          //       ? "rgb(181 247 204)"
                          //       : userList?.find(
                          //           (user) => user.id == row.reviewedBy
                          //         )?.userName || ""
                          //       ? "rgb(243 146 243)"
                          //       : userList?.find(
                          //           (user) => user.id == row.witnessBy
                          //         )?.userName || ""
                          //       ? "#f59b79"
                          //       : userList?.find(
                          //           (user) => user.id == row.approvedby
                          //         )?.userName || ""
                          //       ? "rgb(160 160 247)"
                          //       : userList?.find(
                          //           (user) => user.id == row.rejectedBy
                          //         )?.userName || ""
                          //       ? "#FF3F33"
                          //       : row.calibratedby && (row.totalReadings != null && row.totalReadings > 0) 
                          //       ? "#22c9b0"
                          //       : row.revisionNumber > 0
                          //       ? "#EB4F83"
                          //       : "defaultColor",
                          // }}
                        >
                          <Link to={`/certificate/viewCertificate/${row.id}`} className="custom-link" title="Download Certificate">
                          {row.serviceReqNumber}
                          </Link>
                        </TableCell>
                      <TableCell>
                      <Link to={`/certificate/viewCertificate/${row.id}`} className="custom-link" title="Download Certificate">
                        {row.certificateNumber}
                        </Link>
                        </TableCell>
                        <TableCell>{row.ULRNo}</TableCell>
                        <TableCell
                          // style={{
                          //   backgroundColor:
                          //     userList?.find((user) => user.id == row.signedBy)
                          //       ?.userName || ""
                          //       ? "rgb(181 247 204)"
                          //       : userList?.find(
                          //           (user) => user.id == row.reviewedBy
                          //         )?.userName || ""
                          //       ? "rgb(243 146 243)"
                          //       : userList?.find(
                          //           (user) => user.id == row.witnessBy
                          //         )?.userName || ""
                          //       ? "#f59b79"
                          //       : userList?.find(
                          //           (user) => user.id == row.approvedby
                          //         )?.userName || ""
                          //       ? "rgb(160 160 247)"
                          //       : userList?.find(
                          //           (user) => user.id == row.rejectedBy
                          //         )?.userName || ""
                          //       ? "#FF3F33"
                          //       : row.calibratedby && (row.totalReadings != null && row.totalReadings > 0)
                          //       ? "#22c9b0"
                          //       : row.revisionNumber > 0
                          //       ? "#EB4F83"
                          //       : "defaultColor",
                          // }}
                        >
                          <Link to={`/certificate/viewCertificate/${row.id}`} className="custom-link" title="Download Certificate">
                          {row.jobNumber}
                          </Link>
                        </TableCell>
                      {settingList["companyHierarchy"] ? (
                        <>
                          {companyHierarchyCell?.length > 0 ? (
                            <>
                              <TableCell>
                                {companyHierarchyCell[0]?.trim()}
                              </TableCell>
                              {companyHierarchyCell.length > 1 ? (
                                <TableCell>
                                  {companyHierarchyCell[1]?.trim()}
                                </TableCell>
                              ) : (
                                <TableCell></TableCell>
                              )}
                            </>
                          ) : (
                            <>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                            </>
                          )}
                        </>
                      ) : null}
                      <TableCell>{row.instrumentName}</TableCell>
                      <TableCell>{row?.locationOfInstrument}</TableCell>
                      <TableCell>{parseRange(row.ranges)}</TableCell>
                      <TableCell>{parseRange(row.lc)}</TableCell>
                      <TableCell>{row.DUCID}</TableCell>
                      <TableCell>{row.serialNumber}</TableCell>
                      <TableCell>{row.model}</TableCell>
                      <TableCell style={{ textWrap: "nowrap"}}>{DateisValid(row.calibrationDate)}</TableCell>
                      <TableCell style={{ textWrap: "nowrap"}}>
                      {row?.calFrequency == "Not Required"
                      ? "-"
                      : row?.calFrequency == "Customer Defined"
                      ? "Customer Defined"
                      : row?.calFrequency == "NA"
                      ? "NA"
                      : DateisValid(row?.nextDueDate)}
                      </TableCell>
                      {userType !== "3" && (
                        <TableCell>
                          {userList?.find((user) => user.id == row.calibratedby)?.userName || ""}
                        </TableCell>
                      )}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">                          
                          <Tooltip
                            title="Download Certificate"
                            placement="top-start"
                          >
                            <Button
                              id="certificate-certificatelistprint"
                              component={Link}
                              to={`/certificate/viewCertificate/${row.id}`}
                            >
                              <DownloadIcon />
                            </Button>
                          </Tooltip>
                          <IconButton onClick={(e) => openRowMenu(e, row)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                 <TableCell colSpan={colCount} align="center">
                   <h6><b>Data Not Found</b></h6>
                  </TableCell>
                </TableRow>
              )} 
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          sx={{ "& .MuiTablePagination-toolbar, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select": { fontSize: "11px", }, "& .MuiIconButton-root": { padding: "3px" }, "& svg": { fontSize: "17px" }, }}
          id="certificate-certificatelistpagination"
          rowsPerPageOptions={paginationrowsPerPageOptions}
          count={totalRowsForActiveTab}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          component="div"
          colSpan={3}
          SelectProps={{
            inputProps: {
              "aria-label": "rows per page",
            },
            native: true,
          }}
          ActionsComponent={TablePaginationActions}
        />
      </Box>


      {/* Shared per-row actions menu (Download / Send for Approval / Info / Calibration Time) */}
      <Menu anchorEl={rowMenuAnchorEl} open={Boolean(rowMenuAnchorEl)} onClose={closeRowMenu}
      sx={{ "& .MuiMenuItem-root": { fontSize: "13px", minHeight: "36px", padding: "6px 12px", }, "& .MuiSvgIcon-root": { fontSize: "17px", }, }}
      >
        {rowMenuTarget && (
          <MenuItem
            onClick={() => {
              handleOpenProgress(rowMenuTarget);
              closeRowMenu();
            }}
          >
            <TrendingUpIcon fontSize="small" style={{ marginRight: 8 }} />
            Progress
          </MenuItem>
        )}
        {rowMenuTarget && userType !== "3" && (
          <MenuItem
            disabled={rowMenuTarget?.approvedby == null || rowMenuTarget?.isAmendment == 1}
            onClick={() => {
              if (window.confirm("Are you sure you want to send request to Amendment?")) {
                setSelectedRow(rowMenuTarget);
                setModalOpen(true);
                closeRowMenu();
              }
            }}
          >
            <EditNoteIcon fontSize="small" style={{ marginRight: 8 }} />
            Amendment Request
          </MenuItem>
        )}
        {rowMenuTarget && settingList?.["certificateDatsheetPrint"] === "true" && (
          <MenuItem
            component={Link}
            to={`/datasheet/edit/${rowMenuTarget.id}/${rowMenuTarget.instrumentId || rowMenuTarget.insId}/print`}
            onClick={closeRowMenu}
          >
            <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
            Download Datasheet
          </MenuItem>
        )}
        {rowMenuTarget && settingList?.["AuditSRF"] === "true" && (
          <MenuItem
            onClick={() => {
              handleauditNavigate(rowMenuTarget);
              closeRowMenu();
            }}
          >
            <AssignmentIcon fontSize="small" style={{ marginRight: 8 }} />
            Audit
          </MenuItem>
        )}
        {rowMenuTarget && localStorage.getItem("modules")?.includes("9") && (
          <MenuItem
            onClick={() => {
              setInwardStatusRow(rowMenuTarget);
              closeRowMenu();
            }}
          >
            <InfoIcon fontSize="small" style={{ marginRight: 8 }} />
            Inward Status
          </MenuItem>
        )}
      </Menu>
      

      {renderProgressModal()}
      {ShowRejectModal()}

       <InwardStatusModal
        row={inwardStatusRow}
        open={!!inwardStatusRow}
        onClose={() => setInwardStatusRow(null)}
        />
    </ListWrap>
  );
}


function InwardStatusModal({ row, open, onClose }) {
  const [data, setData] = useState({
    clientDucStatus: null,
    clientRemark: "",
    clientHistoryCardNo: "",
    clientAcceptanceCriteria: "",
    clientDucLocation: "",
    clientPartName: "",
  });

  useEffect(() => {
    if (open && row?.id) {
      const query = `select * from certificatesView where id = ${row.id}`
      axiosWithToken
        .post(BASE_URL + `dynamic`, { query })
        .then((res) => {
          const item = res.data?.[0] || [];

          setData({
            clientDucStatus: item.clientDucStatus || null,
            clientRemark: item.clientRemark || "",
            clientHistoryCardNo: item.clientHistoryCardNo || "",
            clientAcceptanceCriteria: item.clientAcceptanceCriteria || "",
            clientDucLocation: item.clientDucLocation || "",
            clientPartName: item.clientPartName || "",
          });
        })
        .catch((err) => console.log(err));
    }
  }, [open, row?.id]);

  const status = clientDucStatusOptions.find(
    (x) => x.id === Number(data.clientDucStatus)
  );

  const info = [
    ["Remark", data.clientRemark],
    ["History Card No", data.clientHistoryCardNo],
    ["Acceptance Criteria", data.clientAcceptanceCriteria],
    ["DUC Location", data.clientDucLocation],
    ["Part Name", data.clientPartName],
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: "94%", sm: 600 }, maxHeight: "90vh", overflowY: "auto", bgcolor: "#fff", borderRadius: 3, boxShadow: 24, outline: "none",}}
      >
        <Box sx={{ px: 3, py: 2, background: "linear-gradient(135deg, #1976d2, #42a5f5)", color: "#fff", borderRadius: "12px 12px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" fontWeight={700}>Inward Pending Items</Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          <Box sx={{mb: 2.5, p: 2, borderRadius: 2, backgroundColor: "#f5f9ff", border: "1px solid #dbeafe",}}>
            <Typography sx={{ color: "#222", mb: 0.5}}>DUC Status</Typography>
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{ color: status?.id === 1 ? "#16803c" : status?.id === 3 ? "#d32f2f" : "#1976d2",}}>
              {status?.label || "-"}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {info.map(([label, value]) => (
              <Grid
                item
                xs={12}
                sm={label === "Client Acceptance Criteria" ? 12 : 6}
                key={label}
              >
                <Box sx={{ p: 1.5, minHeight: 65, borderRadius: 2, border: "1px solid #e0e0e0", backgroundColor: "#fafafa",}}>
                  <Typography  sx={{ color: value ? "#222" : "#999", mb: 0.5}}> {label}</Typography>

                  <Typography variant="body2"
                    fontWeight={600}
                    sx={{ color: "#777", display: "block", wordBreak: "break-word",}}
                  >
                    {value || "-"}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, pt: 2, borderTop: "1px solid #eee",}}>
            <Button
              variant="contained"
              color="error"
              onClick={onClose}
              startIcon={<CloseIcon />}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}